/**
 * Chat widget view for the login pane/screen.
 */
import springyView from '../../tools/SpringyView.js';
import { focusFirst } from '../../tools/focusUtil.js';
import { createAltchaWidget, resetAltchaWidget } from '../../tools/altchaCaptcha.js';

const loginView = Object.create(springyView);

/**
 * @prop {patronSession} patronSession Data about patron.
 */
loginView.patronSession = null;
/**
 * @prop {Object} $form Jquery form object.
 */
loginView.$form = null;
/**
 * @prop {HTMLElement|null} captchaWidget altcha-widget element when site requires captcha.
 */
loginView.captchaWidget = null;
/**
 * @prop {Boolean} captchaVerified Has the patron verified the captcha.
 */
loginView.captchaVerified = false;

/**
 * @prop {Object} events Default events.
 */
loginView.events = {
    'submit form': 'processLoginForm',
    'click .s-lch-btn-close': 'handleCancelClick',
};
/**
 * @prop {Object} listeners
 */
loginView.listeners = {
    'chatError': 'showLoginError',
    'chatBlockedError': 'showLoginError',
    'captcha:failed': 'showCaptchaError',
};
/**
 * Wrapper for springyView.init in case we need extra functionality.
 * @param {Object} config Config data.
 * @param {chatWidget} config.model Widget object for this view.
 * @param {Object} config.$el Div for this view. $('#login') for now.
 * @param {patronSession} patronSession Patron session object.
 */
loginView.initialize = function (config) {
    if (config.patronSession) {
        this.patronSession = config.patronSession;
    }
    config.$el = window.$('#login');
    this.init(config);
    if (!this.model.isEmbed()) {
        this.addEvent('keydown', 'trapFocus');
    }
};
/**
 * Validate name field.
 * @return {Boolean}
 */
loginView.validateName = function () {
    const $nameInput = this.$form.find('input[name=client_name]');
    let client_name = $nameInput.val().trim();
    // Check name is blank
    if (client_name === '') {
        if (this.model.get('enable_anon')) {
            client_name = this.model.generateAnonName();
            $nameInput.val(client_name);
        } else {
            this.showValidationError($nameInput, this.model.get('valid_form_n'));
            $nameInput.focus();
            return false;
        }
    }
    this.patronSession.set('name', client_name);
    return true;
};
/**
 * Validate contact info.
 * @return {Boolean}
 */
loginView.validateContact = function () {
    if (this.model.get('contact_request')) {
        const $contactInput = this.$form.find('input[name=client_contact]');
        const contact = $contactInput.val().trim();
        // validate
        if (!this.model.validateContact(contact)) {
            this.showValidationError($contactInput, this.model.get('valid_form_c'));
            $contactInput.focus();
            return false;
        }
        // This will reformat and set.
        this.patronSession.setContactInfo(contact);
    }
    return true;
};
/**
 * Validate question.
 * @return {Boolean}
 */
loginView.validateQuestion = function () {
    const $questionInput = this.$form.find('textarea[name=client_question]');
    const client_question = ($questionInput.length === 1) ? $questionInput.val().trim() : '';

    if (this.model.get('initial_question')) {
        if (client_question === '') {
            this.showValidationError($questionInput, this.model.get('valid_form_q'));
            $questionInput.focus();
            return false;
        }
    }
    this.patronSession.set('client_question', this.model.sanitizeMessage(client_question));
    this.model.set('client_question', this.patronSession.get('client_question'));
    return true;
};
/**
 * Parse/validate custom fields.
 * NOTE: code repeated in chatWidgetChatView.contactSave we should probably centralize it somewhere else.
 * @returns {Boolean}
 */
loginView.validateCustomFields = function () {
    const customFields = this.model.get('customFields');
    if (!Array.isArray(customFields) || customFields.length === 0) {
        return true;
    }
    let isValid = true;
    customFields.forEach((field) => {
        if (!field.show) {
            return;
        }
        const fieldInput = this.$form.find(`#s-lch-user${field.id}`);
        if (!fieldInput || fieldInput.length === 0) {
            return;
        }
        const value = fieldInput.val().trim();
        if (field.required) {
            let invalid = false;
            if (field.type === 'l'
                && (!fieldInput[0].selectedIndex || fieldInput[0].selectedIndex <= 0)) {
                // is it unselected or the first empty/0 option.
                invalid = true;
            } else if (value === '') {
                invalid = true;
            }
            if (invalid) {
                isValid = false;
                this.showValidationError(fieldInput, this.model.get('translation').required || 'Required');
                fieldInput.focus();
                return;
            }
        }
        this.patronSession.set(`user${field.id}`, {
            name: field.name,
            val: fieldInput.val().trim(),
        });
    });
    return isValid;
};
/**
 * Validate captcha was verified (if required).
 * @returns {Boolean}
 */
loginView.validateCaptcha = function () {
    if (!this.model.get('use_captcha') || this.captchaVerified) {
        return true;
    }
    this.showCaptchaValidationError(this.model.get('translation').ch_captcha_required || 'Please confirm you are not a robot.');
    return false;
};
/**
 * Show an error message under the captcha.
 * @param {String} msg Message to show.
 */
loginView.showCaptchaValidationError = function (msg) {
    const container = this.$form[0].querySelector('.s-lch-captcha');
    if (!container) {
        return;
    }
    this.$form.find('.s-lch-captcha-error').remove();
    const error = document.createElement('p');
    error.className = 'text-danger s-lch-form-error s-lch-captcha-error';
    error.setAttribute('role', 'alert');
    error.textContent = msg;
    container.after(error);
};
/**
 * Set up the captcha widget in the form.
 */
loginView.loadCaptcha = function () {
    const container = this.$form[0].querySelector('.s-lch-captcha');
    this.captchaVerified = false;
    this.captchaWidget = null;
    // clear any payload from a previous render
    this.model.emitter.trigger('captcha:verified', '');
    if (!container) {
        return;
    }
    this.captchaWidget = createAltchaWidget(container, {
        host: window.location.hostname,
        onVerified: (payload) => {
            this.captchaVerified = true;
            this.$form.find('.s-lch-captcha-error').remove();
            this.model.emitter.trigger('captcha:verified', payload);
        },
        onExpired: () => {
            this.captchaVerified = false;
            this.model.emitter.trigger('captcha:verified', '');
        },
    });
};
/**
 * Captcha was rejected by the server, reset it so they can try again.
 * Form values are kept as is.
 * @param {String} error Error text.
 */
loginView.showCaptchaError = function (error = '') {
    if (this.$form === null) {
        return;
    }
    this.captchaVerified = false;
    if (this.$form[0].querySelector('.s-lch-captcha') === null) {
        // Site turned on captcha after this widget loaded, add it to the form.
        const group = document.createElement('div');
        group.className = 'form-group';
        group.innerHTML = '<div class="s-lch-captcha"></div>';
        this.$submitButton[0].closest('.form-group').before(group);
        this.loadCaptcha();
    } else {
        resetAltchaWidget(this.captchaWidget);
    }
    this.showCaptchaValidationError(error || this.model.get('translation').ch_captcha_required || 'Please confirm you are not a robot.');
    this.$submitButton.prop('disabled', false);
};
/**
 * Process/parse/validate the login form.
 * Return false if validation fails.
 * @param {Object} e Submit event
 * @return {Boolean}
 */
loginView.processLoginForm = function (e) {
    e.preventDefault();
    this.$submitButton.prop('disabled', true);
    this.$form.find('.s-lch-form-error').remove();

    if (!this.validateName()
        || !this.validateContact()
        || !this.validateQuestion()
        || !this.validateCustomFields()
        || !this.validateCaptcha()) {
        this.$submitButton.prop('disabled', false);
        return false;
    }

    // Check what op or dept is set in the form so we know who to connect to
    // Check op first, then dept.
    const $opidField = this.$form.find('*[name=opId]');
    const $deptidField = this.$form.find('*[name=dept]');
    if ($opidField.length > 0) {
        this.patronSession.set('uid', Number($opidField.val()));
    }
    if (this.patronSession.get('uid') < 1 && $deptidField.length > 0) {
        this.patronSession.set('did', Number($deptidField.val()));
    }
    // this will trigger the start session on the controller.
    this.model.set('loggedIn', true);
    return true;
};
/**
 * Show login/session start error.
 * @param {String} error Optional error text
 * @param {Boolean} showContact Should we display the contact form (unused in this view).
 */
loginView.showLoginError = function (error = '') {
    const msg = (!error) ? this.model.getErrorMessage() : error;
    this.$form.replaceWith(`<p class="alert alert-danger">${msg}</p>`);
};
/**
 * Handler: Cancel button (for feedback widget embeds only)
 */
loginView.handleCancelClick = function () {
    this.model.emitter.trigger('closeWidget');
};
/**
 * Show a validation error.
 * @param {Object} $field jquery object of field that error is on.
 * @param {String} msg Message to show.
 */
loginView.showValidationError = function ($field, msg) {
    if (msg === '') {
        return;
    }
    $field.prev('label').append(`<span class="text-danger s-lch-form-error">${msg}</span>`);
};
/**
 * Show this view and set focus as appopriate
 */
loginView.showView = function () {
    this.$el.removeClass('hidden');
    if (this.model.get('auth_id') > 0 && !this.model.get('isAuthValidated')) {
        // When LibAuth overlay is shown we want to make this view hidden for screenreaders
        // And disable any form elements from keyboard interaction.
        // Disable inputs to stop focus
        this.$el[0].querySelectorAll('input,select,button,textarea').forEach((el) => {
            el.disabled = true;
        });
        // hide form for screenreaders
        this.$el[0].setAttribute('aria-hidden', 'true');
        return;
    }
    if (this.model.isEmbed()) {
        return;
    }
    focusFirst(this.el);
};
/**
 * Repopulate the login form after re-render.
 * @param {FormData} formData Form data from previous render
 */
loginView.repopulateForm = function (formData) {
    const fields = [
        'client_name',
        'client_contact',
        'client_question',
        'dept',
        'user1',
        'user2',
        'user3',
    ];
    const form = this.$form[0];
    Array.from(formData.entries()).forEach(([key, value]) => {
        if (fields.indexOf(key) < 0) {
            return;
        }
        const input = form.elements[key];
        if (!input) {
            return;
        }
        // All input, textarea, or select at this point.
        switch (input.tagName) {
            case 'SELECT':
                // Make sure the value is still there as an option.
                // Don't query by value cause it could have chars that would need escaping
                input.querySelectorAll('option').forEach((opt) => {
                    if (opt.value === value) {
                        input.value = value;
                    }
                });
                break;
            default:
                input.value = value;
                break;
        }
    });
};
/**
 * Are we in an iframe (helpful for close btn)
 * @return {Boolean}
 */
loginView.inIframe = function () {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};
/**
 * Render the view
 * @return {autoView}
 */
loginView.render = function () {
    // Grab form values if already filled.
    let formData = null;
    if (this.$form !== null) {
        formData = new FormData(this.$form[0]);
    }

    // clear out existing content, events, etc.
    this.$el.empty();

    // render the template and put it in the dom.
    const templateData = this.model.toJSON();
    templateData.patron = this.patronSession.toJSON();
    templateData.inIframe = this.inIframe();
    this.$el.html(this.templateEng.render('pane_login.njk', templateData));

    this.$form = this.$el.find('#s-lch-login-form');
    this.$submitButton = this.$form.find('button[type=submit]');

    if (formData !== null) {
        this.repopulateForm(formData);
    }

    if (this.model.get('use_captcha')) {
        this.loadCaptcha();
    }

    if (this.model.get('auth_id') > 0 && this.model.get('isAuthValidated')) {
        const clientContact = this.$form[0].querySelector('#client_contact');
        const clientName = this.$form[0].querySelector('#client_name');
        if (clientContact && clientContact.value) {
            clientContact.readOnly = true;
        }
        if (clientName && clientName.value) {
            clientName.readOnly = true;
        }
    }

    return this;
};

export default loginView;
